import { getAssetsPath, PD2 } from "common";

const baseDir = PD2 ? 'pd2/' : '';

const loadBuffer = path => fetch(getAssetsPath(path)).then(r => r.arrayBuffer()).then(b => new Uint8Array(b));

const itemLib = fetch(getAssetsPath(baseDir + 'itemlib.json')).then(r => r.json());
const itemarc = [];
export async function itemdc6(name) {
  if (!name) return null;
  const lib = await itemLib;
  const entry = lib[name.toLowerCase()];
  if (!entry) return null;
  const [index, offset, length] = entry;
  if (!itemarc[index]) {
    itemarc[index] = loadBuffer(baseDir + `items${index}.dcx`);
  }
  const buf = await itemarc[index];
  return buf.subarray(offset, offset + length);
}

const paletteAsync = loadBuffer('pal.dat');
const tintsAsync = loadBuffer('tints.dat');

async function myCreateImageBitmap(data) {
  if (window.createImageBitmap) {
    return window.createImageBitmap(data);
  } else {
    const canvas = document.createElement('canvas');
    canvas.width = data.width;
    canvas.height = data.height;
    canvas.getContext('2d').putImageData(data, 0, 0);
    return canvas;
  }
}

export async function makeBitmap(dc6, tint, frameList) {
  if (!dc6) return null;

  const palette = await paletteAsync;
  const tintTable = tint && await tintsAsync;

  const u32 = new Uint32Array(dc6.buffer, dc6.byteOffset, dc6.length >> 2);
  if (u32[0] !== 6) return null;
  const read32 = pos => {
    if (pos & 3) {
      return dc6[pos] | (dc6[pos + 1] << 8) | (dc6[pos + 2] << 16) | (dc6[pos + 3] << 24);
    } else {
      return u32[pos >> 2];
    }
  };

  const count = u32[4] * u32[5];
  if (!count) return null;

  if (!frameList) {
    frameList = [...Array(count)].map((_, i) => i);
  } else {
    frameList = frameList.filter(i => i >= 0 && i < count);
  }

  const frames = [];
  let curWidth = 0, curHeight = 0, maxWidth = 0, maxHeight = 0;
  for (const i of frameList) {
    const offset = u32[6 + i];
    const width = read32(offset + 4);
    const height = read32(offset + 8);
    if (curWidth && curWidth + width > 512) {
      maxWidth = Math.max(maxWidth, curWidth);
      curWidth = 0;
      maxHeight += curHeight;
      curHeight = 0;
    }
    frames[i] = {x: curWidth, y: maxHeight, width, height};
    curWidth += width;
    curHeight = Math.max(curHeight, height);
  }
  maxWidth = Math.max(maxWidth, curWidth);
  maxHeight += curHeight;

  if (!maxWidth || !maxHeight) return null;

  const imageData = new ImageData(maxWidth, maxHeight);
  const data = imageData.data;

  for (const i of frameList) {
    const offset = u32[6 + i];
    const flip = read32(offset);
    const height = read32(offset + 8);
    let pos = offset + 32;
    const end = read32(offset + 28) + pos;
    let base = (frames[i].x + frames[i].y * maxWidth + (flip ? 0 : maxWidth * (height - 1))) * 4;
    let dst = base;
    while (pos < end) {
      let c = dc6[pos++];
      if (c === 0x80) {
        base = dst = base + (flip ? maxWidth : -maxWidth) * 4;
      } else if (c & 0x80) {
        dst += (c & 0x7F) * 4;
      } else {
        while (c--) {
          let idx = dc6[pos++];
          if (tint) idx = tintTable[tint * 256 + idx];
          data[dst++] = palette[idx * 3 + 2];
          data[dst++] = palette[idx * 3 + 1];
          data[dst++] = palette[idx * 3];
          data[dst++] = 255;
        }
      }
    }
  }
  return {bitmap: await myCreateImageBitmap(imageData), frames};
}

export async function createIcon(name, tint) {
  const dc6 = await itemdc6(name);
  return makeBitmap(dc6, tint);
}

const imageCache = {};
export function getCommonImage(name) {
  if (imageCache[name]) return imageCache[name];
  return imageCache[name] = createIcon(name);
}

async function doLoadFont(name) {
  const [dc6, tbl] = await Promise.all([loadBuffer(`font/${name}.DC6`), loadBuffer(`font/${name}.tbl`)]);

  const results = await Promise.all([...Array(13)].map((_, i) => makeBitmap(dc6, i ? 9 * 21 + i : null)));
  const bitmaps = results.map(r => r.bitmap);
  const frames = results[0].frames;

  const chars = {};
  for (let i = 0; i < 256; ++i) {
    const offset = 12 + i * 14;
    const chr = tbl[offset];
    const width = tbl[offset + 3];
    const height = tbl[offset + 4];
    const frame = tbl[offset + 8];
    chars[chr] = {width, height, frame: frames[frame]};
  }

  return {
    render(ctx, x, y, text, color=0) {
      for (let i = 0; i < text.length; ++i) {
        const chr = text.charCodeAt(i);
        if (chr === 0x5C && text.substring(i + 1, i + 5) === 'xffc') {
          i += 5;
          const code = text.charCodeAt(i);
          if (code >= 0x30 && code <= 0x3C) {
            color = code - 0x30;
          }
        } else if (chr === 0xFF && text.substring(i + 1, i + 2) === 'c') {
          i += 2;
          const code = text.charCodeAt(i);
          if (code >= 0x30 && code <= 0x3C) {
            color = code - 0x30;
          }
        } else {
          if (!chars[chr]) continue;
          const {width, height, frame} = chars[chr];
          ctx.drawImage(bitmaps[color], frame.x, frame.y, frame.width, frame.height, x, y - height, frame.width, frame.height);
          x += width;
        }
      }
    },
    measure(text) {
      let result = 0, maxlen = 0;
      for (let i = 0; i < text.length; ++i) {
        const chr = text.charCodeAt(i);
        if (chr === 0x5C && text[i + 1] === 'n') {
          maxlen = Math.max(maxlen, result);
          result = 0;
          i += 1;
        } else if (chr === 0x5C && text.substring(i + 1, i + 5) === 'xffc') {
          i += 5;
        } else if (chr === 0xFF && text.substring(i + 1, i + 2) === 'c') {
          i += 2;
        } else {
          if (!chars[chr]) continue;
          result += chars[chr].width;
        }
      }
      return Math.max(result, maxlen);
    },
  };
}

const fontCache = {};
export function loadFont(name) {
  if (fontCache[name]) return fontCache[name];
  return fontCache[name] = doLoadFont(name);
}

export function clearCache() {
  itemarc.length = 0;
  for (const id in imageCache) {
    delete imageCache[id];
  }
  for (const id in fontCache) {
    delete fontCache[id];
  }
}

export const socketPositions = {
  '2x1': [
    [0, 1],
    [0, 0,  0, 2],
  ],
  '3x1': [
    [0, 2],
    [0, 1,  0, 3],
    [0, 0,  0, 2,  0, 4],
  ],
  '4x1': [
    [0, 3],
    [0, 2,  0, 4],
    [0, 1,  0, 3,  0, 5],
    [0, 0,  0, 2,  0, 4,  0, 6],
  ],
  '2x2': [
    [1, 1],
    [1, 0,  1, 2],
    [0, 0,  2, 0,  1, 2],
    [0, 0,  2, 2,  0, 2,  2, 0],
  ],
  '3x2': [
    [1, 2],
    [1, 1,  1, 3],
    [1, 0,  1, 2,  1, 4],
    [0, 1,  2, 3,  0, 3,  2, 1],
    [1, 2,  0, 0,  2, 4,  0, 4,  2, 0],
    [0, 0,  0, 2,  0, 4,  2, 0,  2, 2,  2, 4],
  ],
  '4x2': [
    [1, 3],
    [1, 1,  1, 5],
    [1, 1,  1, 3,  1, 5],
    [1, 0,  1, 2,  1, 4,  1, 6],
    [1, 3,  0, 1,  2, 5,  0, 5,  2, 1],
    [0, 1,  0, 3,  0, 5,  2, 1,  2, 3,  2, 5],
  ],
};
